import { jsx as _jsx } from "react/jsx-runtime";
import { HIGHLIGHTS_ITEMS_KEY, HIGHLIGHTS_TITLE_SCALE, resolveTypeScale, } from "@diality/contracts";
import { Highlights } from "../components/blocks/highlights";
/**
 * The bare band, with no block chrome — what a component harness renders, and
 * what a page renders through {@link HighlightsBlock}.
 *
 * The title is passed through EVEN WHEN EMPTY, and that is load-bearing rather
 * than incidental. `HighlightsProps.title` defaults to "At a Glance" — the
 * Product page's own wording — so a view that omitted an empty title would make
 * every authored band silently inherit that heading, and a confirmation looking
 * only for "a title" would pass. `TimelineBlock` passes its eyebrow through for
 * exactly the same reason.
 *
 * `data-highlights-renderer` is deliberately NOT here: it belongs to the
 * harness route. A hardcoded Highlights band already ships on the live /product
 * page, and a renderer hook on this component would make a production marketing
 * page answer to the acceptance drivers' locator. `StatementView` records the
 * same rule.
 */
export function HighlightsBlockView({ data }) {
    return (_jsx(Highlights, { title: data.title, 
        // Resolved here as well as in the mapper, so the band is safe to render
        // from data composed in code that never passed through a stored block.
        titleSize: resolveTypeScale(HIGHLIGHTS_TITLE_SCALE, data.titleSize), items: data.highlights.map((highlight, index) => ({
            key: index,
            label: highlight.label,
            value: highlight.description,
        })) }));
}
/**
 * HighlightsBlock — the "At a Glance" grid of product facts as a composed page
 * holds it.
 *
 * The band draws its own background; BlockWrapper owns its outer spacing.
 */
export function HighlightsBlock({ data }) {
    return _jsx(HighlightsBlockView, { data: data });
}
/**
 * Read a stored Highlights block into what the band draws.
 *
 * `_resolve` is unused and named so: this is the first block in the catalog that
 * holds no media at any level, so there is no reference to resolve. If a
 * highlight ever gains a picture that is a change to the component first and a
 * new story second.
 *
 * A highlight with neither part is dropped. An Editor who has added a row and
 * not yet typed into it has not composed a fact, and the grid has no empty state
 * to draw — a blank card is a hole in a band of type. A HALF-filled row is kept,
 * deliberately: the rules refuse to save one, so a half-filled row on the canvas
 * is work in progress that the Editor should keep seeing.
 */
export function highlightsFromBlock(block, _resolve) {
    const highlights = (block.repeats?.[HIGHLIGHTS_ITEMS_KEY] ?? [])
        .map((item) => ({
        label: item.scalars?.label ?? "",
        description: item.scalars?.description ?? "",
    }))
        .filter((highlight) => highlight.label.trim() !== "" || highlight.description.trim() !== "");
    return {
        title: block.scalars?.title ?? "",
        // A band stored before the size control existed has no such scalar, so it
        // resolves to the block's default — which is deliberately one step LARGER
        // than the 64px it used to draw. See HIGHLIGHTS_TITLE_SCALE.
        titleSize: resolveTypeScale(HIGHLIGHTS_TITLE_SCALE, block.scalars?.titleSize),
        highlights,
    };
}
export const highlightsBlockEntry = {
    Component: HighlightsBlock,
    fromBlock: highlightsFromBlock,
};
//# sourceMappingURL=highlights.js.map